import React, { useState, useEffect } from 'react'
import { SpotifyPreviewContent } from './previews/SpotifyPreviewContent'
import { getApiBase } from '../../utils/api'
import { getQRBranding } from '../../utils/branding'
import { Loader } from 'lucide-react'

interface SpotifyProductionWrapperProps {
  linkId?: string
  linkData?: any
  isPreview?: boolean
  accountSlug?: string
}

export const SpotifyProductionWrapper: React.FC<SpotifyProductionWrapperProps> = ({ 
  linkId, 
  linkData: propLinkData, 
  isPreview = false,
  accountSlug
}) => {
  const [linkData, setLinkData] = useState<any>(propLinkData || null)
  const [branding, setBranding] = useState<any>(null)
  const [loading, setLoading] = useState(!propLinkData)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!linkId || propLinkData) return

      try {
        setLoading(true)
        const API_BASE = getApiBase()
        
        // Use the appropriate endpoint based on whether we have an account slug
        const endpoint = accountSlug 
          ? `${API_BASE}/api/public/link/${accountSlug}/${linkId}`
          : `${API_BASE}/api/public/link/${linkId}`
        
        const response = await fetch(endpoint)
        
        if (!response.ok) {
          throw new Error('Link not found')
        }
        
        const data = await response.json()
        setLinkData(data)
        
        // Load branding
        const brandingConfig = await getQRBranding(data.branding_type, data.author_id)
        setBranding(brandingConfig)
      } catch (err) {
        console.error('Error loading Spotify data:', err)
        setError('Spotify-Playlist konnte nicht geladen werden')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [linkId, propLinkData, accountSlug])

  // Load branding for preview mode
  useEffect(() => {
    const loadBranding = async () => {
      if (!propLinkData || branding) return
      try {
        const brandingConfig = await getQRBranding(
          propLinkData.branding_type || 'beyondpages', 
          propLinkData.author_id
        )
        setBranding(brandingConfig)
      } catch (e) {
        console.error('Error loading branding for Spotify preview:', e)
      }
    }
    
    loadBranding()
  }, [propLinkData, branding])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error || !linkData) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">{error || 'Keine Daten verfügbar'}</p>
      </div>
    )
  }

  // Transform data for SpotifyPreviewContent
  const previewData = {
    title: linkData.title || '',
    description: linkData.description || '',
    target_url: linkData.target_url || '',
    bookTitle: linkData.metadata?.bookTitle || '',
    author: linkData.metadata?.author || '',
    isbn: linkData.metadata?.isbn || '',
    coverUrl: linkData.metadata?.coverUrl || '',
    showBookCover: linkData.metadata?.showBookCover !== false,
    playerTheme: linkData.metadata?.playerTheme || 'dark',
    fonts: linkData.metadata?.fonts || {},
    textSizes: linkData.metadata?.textSizes || {},
    companyPosition: linkData.metadata?.companyPosition || 'bottom'
  }

  // Extract custom colors from metadata
  const customColors = linkData.metadata?.customColors || {
    primaryColor: branding?.primaryColor || '#3B82F6',
    secondaryColor: branding?.secondaryColor || '#DBEAFE',
    textColor: branding?.textColor || '#FFFFFF',
    wrapperColor: linkData.metadata?.customColors?.wrapperColor,
    wrapperOpacity: linkData.metadata?.customColors?.wrapperOpacity
  }

  // Determine color mode from metadata or use default
  const colorMode = linkData.metadata?.colorMode || 'gradient'

  // Transform branding data
  const userBranding = branding ? {
    companyName: branding.companyName || '',
    logoUrl: branding.logoUrl || '',
    primaryColor: branding.primaryColor || '#3B82F6',
    secondaryColor: branding.secondaryColor || '#DBEAFE',
    textColor: branding.textColor || '#FFFFFF',
    displayType: branding.displayType || 'logo_and_name'
  } : undefined

  return (
    <SpotifyPreviewContent 
      data={previewData}
      customColors={customColors}
      colorMode={colorMode}
      brandingType={linkData.branding_type || 'beyondpages'}
      userBranding={userBranding}
      isPreview={isPreview}
    />
  )
}
